import { useParams, Link } from 'react-router-dom';
import { articles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOMeta from '@/components/feature/SEOMeta';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);
  const categoryArticles = articles.filter((a) => a.categoryId === category?.id);

  if (!category) {
    return (
      <>
        <Navbar />
        <main className="pt-24 px-4 md:px-8 lg:px-12 min-h-[60vh] flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-foreground-900 mb-4">分类不存在</h1>
            <Link to="/" className="text-accent-500 hover:underline">返回首页</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: category.name },
  ];

  const webpageStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${category.name} - 五一吃瓜网`,
    url: `http://xjqkfyv.cn/category/${category.slug}`,
    description: category.description,
    publisher: {
      '@type': 'Organization',
      name: '五一吃瓜网',
    },
    datePublished: '2026-07-02',
    dateModified: '2026-07-02',
  };

  const itemListStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    itemListElement: categoryArticles.map((article, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      url: `http://xjqkfyv.cn/article/${article.slug}`,
      name: article.title,
    })),
  };

  return (
    <>
      <SEOMeta
        title={`${category.name} - 五一吃瓜网 | ${category.name}爆料资讯大全`}
        description={category.description}
        keywords={`${category.name},五一吃瓜网,${category.name}爆料,${category.name}最新资讯,吃瓜不打烊`}
        canonical={`http://xjqkfyv.cn/category/${category.slug}`}
        structuredData={[webpageStructuredData, itemListStructuredData]}
      />

      <Navbar />

      <header className="relative w-full h-[400px] md:h-[500px] overflow-hidden mt-16 md:mt-20">
        <img
          src="https://readdy.ai/api/search-image?query=dramatic%20entertainment%20gossip%20news%20scene%20with%20warm%20amber%20spotlights%20and%20dark%20moody%20atmosphere%2C%20crowded%20press%20conference%20vibe%2C%20cinematic%20editorial%20photography%20with%20rich%20warm%20tones&width=1600&height=600&seq=cat-hero-generic&orientation=landscape"
          alt={`${category.name} - 五一吃瓜网`}
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/50" />
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4">
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white font-heading mb-3">
            {category.name}
          </h1>
          <p className="text-base md:text-lg text-white/80 max-w-xl font-light">
            {category.description}
          </p>
          <span className="mt-4 text-sm text-accent-300 bg-accent-500/20 px-3 py-1 rounded-full">
            {category.articleCount} 篇爆料
          </span>
        </div>
      </header>

      <main className="px-4 md:px-8 lg:px-12 py-8 md:py-12">
        <div className="max-w-6xl mx-auto">
          <Breadcrumb items={breadcrumbItems} />

          <div className="flex flex-col md:flex-row md:items-end justify-between mb-8 md:mb-10 gap-4 mt-4">
            <div>
              <h2 className="text-xl md:text-2xl lg:text-3xl font-bold text-foreground-500 font-heading">
                {category.name}
              </h2>
              <h3 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-900 font-heading italic">
                最新爆料
              </h3>
            </div>
            <p className="text-sm text-foreground-600 max-w-md leading-relaxed">
              {category.description}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {categoryArticles.map((article) => (
              <article
                key={article.id}
                className="group bg-background-50 rounded-2xl overflow-hidden hover:shadow-lg transition-all duration-300"
              >
                <Link to={`/article/${article.slug}`} className="block">
                  <div className="relative h-48 md:h-56 overflow-hidden">
                    <img
                      src={article.coverImage}
                      alt={article.title}
                      title={`${article.title} | 五一吃瓜网`}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
                  </div>
                  <div className="p-4 md:p-5">
                    <span className="inline-block bg-accent-500 text-background-50 text-xs font-medium px-2.5 py-1 rounded-md mb-3">
                      {article.categoryName}
                    </span>
                    <h4 className="text-sm md:text-base font-bold text-foreground-900 leading-snug group-hover:text-accent-500 transition-colors line-clamp-3">
                      {article.title}
                    </h4>
                    <div className="flex items-center gap-3 mt-3 text-xs text-foreground-500">
                      <span>{article.publishedAt}</span>
                      <span className="w-1 h-1 rounded-full bg-foreground-300" />
                      <span>{article.readTime}阅读</span>
                      <span className="w-1 h-1 rounded-full bg-foreground-300" />
                      <span>{(article.views / 10000).toFixed(1)}万阅读</span>
                    </div>
                  </div>
                </Link>
              </article>
            ))}
          </div>

          {categoryArticles.length === 0 && (
            <div className="text-center py-16">
              <p className="text-foreground-500 text-base">该分类暂无文章</p>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </>
  );
}